// Proficiency Level Mapping
// Maps user-friendly labels to internal 1-5 scale

export interface ProficiencyLevel {
    value: number;
    label: string;
    userLabel: string;
    description: string;
    icon: string;
}

// User-facing proficiency levels (simplified)
export const USER_PROFICIENCY_LEVELS: ProficiencyLevel[] = [
    {
        value: 1,
        label: 'Beginner',
        userLabel: 'Just Starting',
        description: 'I\'m new to this and learning',
        icon: 'academy' // 3D icon type
    },
    {
        value: 2,
        label: 'Capable',
        userLabel: 'I Can Do This',
        description: 'I can handle basic tasks',
        icon: 'skills' // 3D icon type
    },
    {
        value: 3,
        label: 'Experienced',
        userLabel: 'I\'m Good at This',
        description: 'I do this regularly and confidently',
        icon: 'talent' // 3D icon type
    },
    {
        value: 4,
        label: 'Expert',
        userLabel: 'I\'m Very Skilled',
        description: 'I can teach others and solve complex problems',
        icon: 'aihub' // 3D icon type - represents mastery
    }
];

// Internal proficiency levels (for backend compatibility)
export const INTERNAL_PROFICIENCY_LEVELS = [
    { level: 1, label: 'Beginner', description: 'Basic understanding, limited experience' },
    { level: 2, label: 'Novice', description: 'Some experience, can work with guidance' },
    { level: 3, label: 'Intermediate', description: 'Solid experience, can work independently' },
    { level: 4, label: 'Advanced', description: 'Extensive experience, can mentor others' },
    { level: 5, label: 'Expert', description: 'Deep expertise, thought leader in the field' },
];

/**
 * Map user selection to internal proficiency level
 * User selects 1-4, we map to 1-5 internal scale
 */
export function mapUserToInternalProficiency(userLevel: number): number {
    const mapping: Record<number, number> = {
        1: 1,  // Just Starting → Beginner
        2: 3,  // I Can Do This → Intermediate
        3: 4,  // I'm Good → Advanced
        4: 5   // I'm Very Skilled → Expert
    };

    return mapping[userLevel] || 3; // Default to Intermediate if unknown
}

/**
 * Map internal proficiency level back to user-friendly level
 */
export function mapInternalToUserProficiency(internalLevel: number): number {
    if (internalLevel <= 1) return 1;  // Beginner → Just Starting
    if (internalLevel <= 2) return 2;  // Novice/Intermediate → I Can Do This
    if (internalLevel <= 4) return 3;  // Advanced → I'm Good
    return 4;  // Expert → I'm Very Skilled
}

/**
 * Get proficiency description for display
 */
export function getProficiencyDescription(level: number, isUserFacing: boolean = true): string {
    if (isUserFacing) {
        const userLevel = USER_PROFICIENCY_LEVELS.find(p => p.value === level);
        return userLevel ? userLevel.description : 'Unknown level';
    }

    const internalLevel = INTERNAL_PROFICIENCY_LEVELS.find(p => p.level === level);
    return internalLevel ? internalLevel.description : 'Unknown level';
}

/**
 * Get proficiency label
 */
export function getProficiencyLabel(level: number, isUserFacing: boolean = true): string {
    if (isUserFacing) {
        const userLevel = USER_PROFICIENCY_LEVELS.find(p => p.value === level);
        return userLevel ? userLevel.userLabel : 'Unknown';
    }

    const internalLevel = INTERNAL_PROFICIENCY_LEVELS.find(p => p.level === level);
    return internalLevel ? internalLevel.label : 'Unknown';
}

export default {
    USER_PROFICIENCY_LEVELS,
    INTERNAL_PROFICIENCY_LEVELS,
    mapUserToInternalProficiency,
    mapInternalToUserProficiency,
    getProficiencyDescription,
    getProficiencyLabel
};
